import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import i18n from '../../../i18n';
import { resetConversationHubConnection } from '../../conversations/services/conversationHub';
import { authService } from '../services/authService';
import {
  AUTH_SESSION_REFRESHED_EVENT,
  logoutAuthSession,
  RefreshSessionResult,
  refreshAuthSession,
} from '../../../shared/services/authRefresh';
import {
  clearAuthSession,
  ClientAuthSession,
  getActiveSession,
  purgeLegacyAuthStorage,
  setActiveSession,
} from '../../../shared/services/authSession';
import { isTenantlessAuthHost } from '../../../shared/config/workspaceHost';
import { consumeWorkspaceHandoff } from '../../workspaces/services/workspaceService';

export type AuthState = ClientAuthSession;

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  auth: AuthState | null;
  permissions: string[];
  hasPermission: (required: string | readonly string[]) => boolean;
  login: (username: string, password: string) => Promise<void>;
  completeSsoLogin: (code: string) => Promise<void>;
  completeWorkspaceHandoff: (code: string, tenantId?: string | null) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [auth, setAuth] = useState<AuthState | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const bootstrapSession = async () => {
      purgeLegacyAuthStorage();

      // The global auth host resolves no workspace, so there is no session to restore there.
      if (isTenantlessAuthHost()) {
        setIsLoading(false);
        return;
      }

      try {
        const refreshed = await refreshAuthSession();
        if (cancelled) {
          return;
        }

        setAuth(refreshed);
      } catch {
        if (!cancelled) {
          clearAuthSession();
          setAuth(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void bootstrapSession();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      setAuth(null);
      clearAuthSession();
    };

    const handleSessionRefreshed = (event: Event) => {
      const detail = (event as CustomEvent<RefreshSessionResult>).detail;
      if (!detail?.tenantId) {
        return;
      }

      setAuth(detail);
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    window.addEventListener(AUTH_SESSION_REFRESHED_EVENT, handleSessionRefreshed);
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      window.removeEventListener(AUTH_SESSION_REFRESHED_EVENT, handleSessionRefreshed);
    };
  }, []);

  const permissions = useMemo(() => auth?.permissions ?? getActiveSession()?.permissions ?? [], [auth]);

  const hasPermission = React.useCallback((required: string | readonly string[]) => {
    if (!permissions.length) {
      return false;
    }

    const requiredList = Array.isArray(required) ? required : [required];
    const available = new Set(permissions);

    return requiredList.some(item => available.has(item.toLowerCase()));
  }, [permissions]);

  const applySession = React.useCallback((session: AuthState) => {
    if (!session.tenantId) {
      throw new Error(i18n.t('auth:errors.noAccessibleTenant'));
    }

    setActiveSession(session);
    setAuth(session);
  }, []);

  const login = React.useCallback(async (username: string, password: string) => {
    applySession(await authService.login({ username, password }));
  }, [applySession]);

  const completeSsoLogin = React.useCallback(async (code: string) => {
    applySession(await authService.completeSso(code));
  }, [applySession]);

  const completeWorkspaceHandoff = React.useCallback(async (code: string, tenantId?: string | null) => {
    applySession(await consumeWorkspaceHandoff(code, tenantId));
  }, [applySession]);

  const logout = () => {
    setAuth(null);
    void logoutAuthSession();
    void resetConversationHubConnection();
  };

  const value = useMemo<AuthContextType>(
    () => ({
      isAuthenticated: !!auth?.tenantId,
      isLoading,
      auth,
      permissions,
      hasPermission,
      login,
      completeSsoLogin,
      completeWorkspaceHandoff,
      logout,
    }),
    [auth, completeSsoLogin, completeWorkspaceHandoff, hasPermission, isLoading, login, permissions]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
